import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import {
  Zap, CheckCircle2, XCircle, AlertTriangle, Clock,
  ChevronDown, Loader2, Send, FlaskConical, Package,
  Hammer, RotateCcw, X, Server, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

/* ═══════════════════════════════════════════════════════════ */

const STATUS_STYLES = {
  running:  { label: 'Running',  cls: 'bg-sky-500/15 text-sky-400 border-sky-500/20',      icon: Loader2 },
  pending:  { label: 'Pending',  cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/20', icon: XCircle },
  failed:   { label: 'Failed',   cls: 'bg-red-500/15 text-red-400 border-red-500/20',       icon: AlertTriangle },
};

const TYPE_STYLES = {
  armory_intake:  { label: 'Armory Intake',  icon: Hammer,       color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  tc_regression:  { label: 'TC Regression',  icon: FlaskConical, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' },
  armory_scan:    { label: 'Armory Scan',    icon: Package,      color: 'text-teal-400 bg-teal-500/10 border-teal-500/20' },
  armory_drift:   { label: 'Drift Check',    icon: AlertTriangle,color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
};

const ENV_BADGE = {
  claude_ai: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  claude_code: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  compass: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  multi: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
};

const TIER_BADGE = {
  1: 'bg-red-500/10 text-red-400 border-red-500/20',
  2: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  3: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  4: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};
const TIER_LABEL = { 1: 'KEEP', 2: 'BATCH', 3: 'NOTIFY', 4: 'AUTO' };

function getTypeStyle(type) { return TYPE_STYLES[type] || { label: type, icon: Zap, color: 'text-slate-400 bg-slate-500/10 border-slate-500/20' }; }

function timeAgo(d) {
  if (!d) return '';
  var m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  var h = Math.floor(m / 60);
  return h < 24 ? h + 'h ago' : Math.floor(h / 24) + 'd ago';
}

function parseJ(s, fb) { try { return typeof s === 'string' ? JSON.parse(s) : (s || fb); } catch(e) { return fb; } }


/* ═══════════════════════════════════════════════════════════
   TASK CARD
   ═══════════════════════════════════════════════════════════ */

function TaskCard({ task, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  const StatusIcon = statusCfg.icon;
  const typeCfg = getTypeStyle(task.task_type);
  const TypeIcon = typeCfg.icon;
  const isPending = task.status === 'pending';

  var resultData = parseJ(task.result_payload, null);
  var downstream = parseJ(task.downstream_action, null);
  var installSteps = downstream?.create_asset?.install_checklist ? parseJ(downstream.create_asset.install_checklist, []) : [];
  var proposedRules = downstream?.create_rules || [];
  var conflicts = downstream?.conflict_resolution || [];

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.2}}>
      <div className={`rounded-xl border bg-slate-900/50 transition-colors ${isPending ? 'border-amber-500/30 hover:border-amber-500/50' : 'border-slate-800/80 hover:border-slate-700/80'}`}>
        <div className="flex items-start">
          <button onClick={()=>setExpanded(!expanded)} className="flex-1 p-4 flex items-start gap-3 text-left min-w-0">
            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${typeCfg.color}`}>
              <TypeIcon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`text-[10px] border ${typeCfg.color}`}>{typeCfg.label}</Badge>
                <Badge variant="outline" className={`text-[10px] border ${statusCfg.cls}`}>
                  <StatusIcon className={`w-3 h-3 mr-1 ${task.status === 'running' ? 'animate-spin' : ''}`} />{statusCfg.label}
                </Badge>
                {installSteps.length > 0 && <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500">{installSteps.length} install steps</Badge>}
                {proposedRules.length > 0 && <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500">{proposedRules.length} rules</Badge>}
                {conflicts.length > 0 && <Badge variant="outline" className="text-[10px] border-red-500/20 text-red-400">{conflicts.length} conflicts</Badge>}
              </div>
              {task.result_summary && <p className="text-sm text-slate-300 mt-2 leading-relaxed">{task.result_summary}</p>}
              <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-600">
                <span>{timeAgo(task.created_date)}</span>
                {task.duration_ms > 0 && <span>· {(task.duration_ms / 1000).toFixed(1)}s</span>}
                {task.tokens_used > 0 && <span>· {task.tokens_used.toLocaleString()} tokens</span>}
                {task.trigger_source && <span>· {task.trigger_source}</span>}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 flex-shrink-0 mt-2 ${expanded ? 'rotate-180' : ''}`} />
          </button>
          {isPending && (
            <div className="flex items-center gap-1.5 p-3 flex-shrink-0">
              <Button data-testid="fb-approve-btn" size="sm" onClick={()=>onApprove(task)} className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/25 h-8 px-3 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
              </Button>
              <Button data-testid="fb-reject-btn" size="sm" onClick={()=>onReject(task)} variant="outline" className="border-slate-700 text-slate-500 hover:text-red-400 hover:border-red-500/30 h-8 px-3 text-xs">
                <XCircle className="w-3.5 h-3.5 mr-1" />Reject
              </Button>
            </div>
          )}
        </div>

        <AnimatePresence>
          {expanded && (resultData || task.error_message) && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}} className="overflow-hidden">
              <div className="px-4 pb-4 border-t border-slate-800/60 pt-3 space-y-4">

                {/* 7-step protocol */}
                {resultData?.steps && resultData.steps.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">7-step protocol</p>
                    {resultData.steps.map(function(s) {
                      return (
                        <div key={s.step} className="flex items-start gap-2 p-2 rounded-lg bg-slate-800/25 border border-slate-800/40">
                          <div className="w-4 h-4 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-[9px] font-bold text-amber-500/80">{s.step}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-medium text-slate-300">{s.label}: </span>
                            <span className="text-xs text-slate-500">{s.detail}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Install checklist preview */}
                {installSteps.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">Install checklist · {installSteps.length} steps</p>
                    {installSteps.map(function(s, i) {
                      return (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-800/25 border border-slate-800/40">
                          <Badge variant="outline" className={`text-[9px] border flex-shrink-0 mt-0.5 ${ENV_BADGE[s.env] || 'border-slate-700 text-slate-500'}`}>{s.env}</Badge>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-slate-300">{s.label}</p>
                            {s.command && <p className="text-[11px] font-mono text-slate-500 mt-0.5">{s.command}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Proposed autonomy rules */}
                {proposedRules.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-medium text-slate-600 uppercase tracking-wider">Proposed autonomy rules · {proposedRules.length}</p>
                    {proposedRules.map(function(r, i) {
                      return (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/25 border border-slate-800/40">
                          <Badge variant="outline" className={`text-[9px] font-bold border flex-shrink-0 ${TIER_BADGE[r.tier] || ''}`}>T{r.tier} {TIER_LABEL[r.tier]}</Badge>
                          <span className="text-xs text-slate-300 flex-1">{r.decision_text || r.capability}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Conflict resolution */}
                {conflicts.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-medium text-red-400/70 uppercase tracking-wider">Conflict resolution · {conflicts.length}</p>
                    {conflicts.map(function(c, i) {
                      return (
                        <div key={i} className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/15">
                          <p className="text-xs text-red-400 font-medium">{c.capability} — conflicts with {c.conflicting_tool}</p>
                          <p className="text-xs text-slate-400 mt-1">{c.resolution}</p>
                          <Badge variant="outline" className="text-[9px] border-red-500/20 text-red-400/70 mt-1">{c.action}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Complementarity matrix */}
                {resultData?.decision && (
                  <div className="flex items-center gap-5">
                    {[{l:'Unique',v:resultData.unique_count},{l:'Complementary',v:resultData.complementary_count},{l:'Redundant',v:resultData.redundant_count},{l:'Conflicting',v:resultData.conflicting_count}].map(function(m) {
                      return (<div key={m.l} className="flex items-baseline gap-1"><span className={`text-base font-bold ${(m.v||0)>0?'text-slate-200':'text-slate-700'}`}>{m.v||0}</span><span className="text-[10px] text-slate-600">{m.l}</span></div>);
                    })}
                  </div>
                )}

                {/* Autonomy impact */}
                {resultData?.autonomy_impact && (
                  <div className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/30">
                    <p className="text-[10px] text-slate-600 font-medium uppercase tracking-wider mb-0.5">Autonomy impact</p>
                    <p className="text-xs text-slate-400">{resultData.autonomy_impact}</p>
                  </div>
                )}

                {/* Generic fallback */}
                {resultData && !resultData.decision && (
                  <pre className="text-xs text-slate-500 font-mono bg-slate-800/30 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">{JSON.stringify(resultData, null, 2)}</pre>
                )}

                {/* Error */}
                {task.error_message && (
                  <div className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/20">
                    <p className="text-xs text-red-400">{task.error_message}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════════
   MAIN PANEL
   ═══════════════════════════════════════════════════════════ */

export default function TaskRunnerPanel() {
  const queryClient = useQueryClient();
  const [intakeInput, setIntakeInput] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [lastError, setLastError] = useState(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['autonomousTasks'],
    queryFn: async () => { try { return await base44.entities.AutonomousTask.list('-created_date', 50); } catch(e) { return []; } },
    initialData: [],
    refetchInterval: 10000,
  });

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const recentTasks = tasks.filter(t => t.status !== 'pending');

  /* ─── TRIGGER ─── */
  const triggerTask = useMutation({
    mutationFn: async (params) => {
      console.log('[TaskRunner] Invoking:', params);
      setLastError(null);
      var resp = await base44.functions.invoke('autonomousTaskRunner', params);
      console.log('[TaskRunner] Raw:', resp);
      var result = resp?.data || resp;
      if (result && result.success === false) throw new Error(result.error || result.detail || 'Task runner error');
      return result;
    },
    onSuccess: (data) => {
      console.log('[TaskRunner] Success:', data);
      queryClient.invalidateQueries({ queryKey: ['autonomousTasks'] });
      toast.success('Task submitted', { description: data?.summary || data?.task_id || 'Processing...' });
    },
    onError: (err) => {
      console.error('[TaskRunner] Error:', err);
      setLastError(err?.message || String(err));
      toast.error('Task failed', { description: err?.message || String(err) });
    }
  });

  /* ─── APPROVE / REJECT — ENG-C: per-create error boundaries ─── */
  const updateTask = useMutation({
    mutationFn: async ({ task, newStatus }) => {
      await base44.entities.AutonomousTask.update(task.id, {
        status: newStatus, reviewed_by: 'commander', reviewed_at: new Date().toISOString()
      });

      var results = { asset: null, eval: null, rules_created: 0, rules_failed: 0, errors: [] };

      if (newStatus === 'approved') {
        var downstream = parseJ(task.downstream_action, null);

        if (downstream?.type === 'armory_intake_approve') {
          /* Create asset */
          try {
            if (downstream.create_asset) {
              results.asset = await base44.entities.ArmoryAsset.create(downstream.create_asset);
            }
          } catch(e) { results.errors.push('Asset create failed: ' + e.message); }

          /* Create evaluation */
          try {
            if (downstream.create_evaluation) {
              results.eval = await base44.entities.ArmoryEvaluation.create({ ...downstream.create_evaluation, approval_status: 'approved' });
            }
          } catch(e) { results.errors.push('Evaluation create failed: ' + e.message); }

          /* ENG-B: Create autonomy rules with dedup */
          if (downstream.create_rules && downstream.create_rules.length > 0) {
            var existingRules = [];
            try { existingRules = await base44.entities.ArmoryAutonomyRule.list(); } catch(e) {}
            var existingIds = new Set(existingRules.map(function(r) { return r.rule_id; }));

            for (var i = 0; i < downstream.create_rules.length; i++) {
              var rule = downstream.create_rules[i];
              if (existingIds.has(rule.rule_id)) {
                results.rules_created++;
                continue;
              }
              try {
                await base44.entities.ArmoryAutonomyRule.create(rule);
                results.rules_created++;
              } catch(e) {
                results.rules_failed++;
                results.errors.push('Rule ' + rule.rule_id + ' failed: ' + e.message);
              }
            }
          }
        }
      }
      return { status: newStatus, results: results };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['autonomousTasks'] });
      queryClient.invalidateQueries({ queryKey: ['armoryAssets'] });
      queryClient.invalidateQueries({ queryKey: ['armoryEvaluations'] });
      queryClient.invalidateQueries({ queryKey: ['armoryRules'] });

      if (data.status === 'approved') {
        var r = data.results;
        if (r.errors.length > 0) {
          toast.warning('Approved with issues', { description: r.errors.join('; ') });
        } else {
          var desc = 'Asset + evaluation created';
          if (r.rules_created > 0) desc += ', ' + r.rules_created + ' autonomy rules';
          toast.success('Task approved', { description: desc });
        }
      } else {
        toast.success('Task rejected');
      }
    },
    onError: (err) => toast.error('Failed to update task', { description: err.message })
  });

  const handleApprove = (task) => setConfirmAction({ task, action: 'approved', label: task.result_summary || task.task_type });
  const handleReject = (task) => setConfirmAction({ task, action: 'rejected', label: task.result_summary || task.task_type });
  const executeConfirm = () => { if (confirmAction) { updateTask.mutate({ task: confirmAction.task, newStatus: confirmAction.action }); setConfirmAction(null); } };

  const handleIntakeSubmit = () => {
    if (!intakeInput.trim()) return;
    triggerTask.mutate({ task_type: 'armory_intake', payload: { description: intakeInput.trim() }, trigger_source: 'button' });
    setIntakeInput('');
  };

  const handleRunRegression = () => { triggerTask.mutate({ task_type: 'tc_regression', payload: {}, trigger_source: 'button' }); };

  var todayStr = new Date().toISOString().slice(0, 10);
  var approvedToday = tasks.filter(t => t.status === 'approved' && (t.reviewed_at || '').startsWith(todayStr)).length;
  var totalTokens = tasks.reduce(function(s, t) { return s + (t.tokens_used || 0); }, 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-xl border border-slate-800/80 bg-slate-900/40">
          <p className={`text-xl font-bold ${pendingTasks.length > 0 ? 'text-amber-400' : 'text-slate-500'}`}>{pendingTasks.length}</p>
          <p className="text-[11px] text-slate-500">Pending review</p>
        </div>
        <div className="p-3 rounded-xl border border-slate-800/80 bg-slate-900/40">
          <p className="text-xl font-bold text-emerald-400">{approvedToday}</p>
          <p className="text-[11px] text-slate-500">Approved today</p>
        </div>
        <div className="p-3 rounded-xl border border-slate-800/80 bg-slate-900/40">
          <p className="text-xl font-bold text-slate-300">{tasks.length}</p>
          <p className="text-[11px] text-slate-500">Total tasks</p>
        </div>
        <div className="p-3 rounded-xl border border-slate-800/80 bg-slate-900/40">
          <p className="text-xl font-bold text-slate-300">{totalTokens > 1000 ? (totalTokens/1000).toFixed(1)+'k' : totalTokens}</p>
          <p className="text-[11px] text-slate-500">Tokens used</p>
        </div>
      </div>

      {/* Triggers */}
      <div className="p-4 rounded-xl border border-slate-800/80 bg-slate-900/40 space-y-3">
        <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider">Drop a job</p>
        <div className="flex items-center gap-2">
          <Input data-testid="fb-taskrunnerpanel-input" value={intakeInput} onChange={e => setIntakeInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleIntakeSubmit(); }}
            placeholder="Paste a URL, tool name, or description to evaluate..."
            className="bg-slate-800/60 border-slate-700/60 text-slate-200 text-sm h-9 placeholder:text-slate-600 flex-1" />
          <Button size="sm" onClick={handleIntakeSubmit} disabled={!intakeInput.trim() || triggerTask.isPending}
            className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30 h-9">
            {triggerTask.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" />Evaluate</>}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="fb-run-regression-btn" size="sm" variant="outline" onClick={handleRunRegression} disabled={triggerTask.isPending}
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 h-8 text-xs">
            <FlaskConical className="w-3.5 h-3.5 mr-1" />Run regression
          </Button>
          <span className="text-[11px] text-slate-600">Runs active test suite and reports results here</span>
        </div>

        {lastError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-red-400">Task failed</p>
              <p className="text-xs text-red-400/70 mt-0.5 break-all">{lastError}</p>
            </div>
            <button onClick={() => setLastError(null)} className="text-red-400/50 hover:text-red-400 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* Pending */}
      {pendingTasks.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <p className="text-sm font-semibold text-slate-200">Pending review</p>
            <span className="text-[11px] text-amber-400/70 bg-amber-500/8 px-2 py-0.5 rounded border border-amber-500/15">{pendingTasks.length}</span>
          </div>
          {pendingTasks.map(task => <TaskCard key={task.id} task={task} onApprove={handleApprove} onReject={handleReject} />)}
        </div>
      )}

      {/* Recent */}
      {recentTasks.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-400">Recent tasks</p>
          {recentTasks.slice(0, 20).map(task => <TaskCard key={task.id} task={task} onApprove={handleApprove} onReject={handleReject} />)}
        </div>
      )}

      {/* Empty */}
      {tasks.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Zap className="w-8 h-8 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No autonomous tasks yet.</p>
          <p className="text-slate-600 text-xs mt-1">Paste a tool above to run your first intake evaluation.</p>
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-200">{confirmAction?.action === 'approved' ? 'Approve this task?' : 'Reject this task?'}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmAction?.action === 'approved'
                ? <span>This will create the asset, evaluation, and autonomy rules for <span className="font-semibold text-slate-300">{confirmAction?.label}</span>.</span>
                : <span>This will mark <span className="font-semibold text-slate-300">{confirmAction?.label}</span> as rejected.</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeConfirm}
              className={confirmAction?.action === 'approved' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'}>
              {confirmAction?.action === 'approved' ? 'Approve' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}